import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem, styled,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import Select, {SelectChangeEvent} from '@mui/material/Select';
import React, {useState} from "react";
import View from "../../components/View";
import Page from "../../components/Page";

interface Recipe {
    gas: string;
    blendingMethod: string;
    havePO2: number;
    havePressure: number;
    bleedTo: number;
    o2Pressure: number;
    contBlend: number
    wantPO2: number;
    wantPressure: number;
}

const Nitrox = () => {

    const [startMix, setStartMix] = useState(30);
    const [startPressure, setStartPressure] = useState(60);
    const [targetMix, setTargetMix] = useState(32);
    const [targetPressure, setTargetPressure] = useState(232);
    const [blendingMethod, setBlendingMethod] = useState('partial');
    const [recipe, setRecipe] = useState<Recipe>({
        bleedTo: 0,
        blendingMethod: "",
        contBlend: 0,
        gas: "",
        havePO2: 0,
        havePressure: 0,
        o2Pressure: 0,
        wantPO2: 0,
        wantPressure: 0
    })
    const [alert, setAlert] = useState(false)
    const [alertDescription, setAlertDescription] = useState('')

    const changeStartMix = (event: SelectChangeEvent) => {
        const value = parseFloat(event.target.value);
        setStartMix(value)
    }

    const changeStartPressure = (event: SelectChangeEvent) => {
        const value = parseInt(event.target.value);
        setStartPressure(value)
    }

    const changeTargetMix = (event: SelectChangeEvent) => {
        const value = parseFloat(event.target.value);
        setTargetMix(value)
    }

    const changeTargetPressure = (event: SelectChangeEvent) => {
        const value = parseInt(event.target.value);
        setTargetPressure(value)
    }

    const changeAlert = (description: string) => {
        setAlertDescription(description)
        setAlert(true);
    }

    const clearAlert = (): void => {
        setAlert(false)
        setAlertDescription('')
    }

    const [open, setOpen] = React.useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // @ts-ignore
    const changeBlendingMethod = (event) => {
        setBlendingMethod(event.target.value)
    }

    const needBleed = (startMix: number, startPressure: number, targetMix: number, targetPressure: number): string => {
        return (((targetPressure * (targetMix - 0.21)) - (startPressure * (startMix - 0.21))) / 0.79).toFixed(1);
    }

    const bleedOf = (startMix: number, targetMix: number, targetPressure: number): string => {
        return ((targetPressure * (targetMix - 0.21)) / (startMix - 0.21)).toFixed(1)
    }

    const calc = (): void => {
        if (blendingMethod === 'partial') {
            calcPartialBlending(Number(startMix / 100), Number(startPressure), Number(targetMix / 100), Number(targetPressure));
        } else {
            calcContinuousBlending(Number(startMix / 100), Number(startPressure), Number(targetMix / 100), Number(targetPressure));
        }
    }

    const calcPartialBlending = (havePO2: number, havePressure: number, wantPO2: number, wantPressure: number): void => {
        const bleedTo = needBleed(havePO2, havePressure, wantPO2, wantPressure);
        const bleed = bleedOf(havePO2, wantPO2, wantPressure);
        let startPressure = havePressure;
        if (Number(bleedTo) < 0) {
            startPressure = Number(bleed);
        }

        let o2 = ((((wantPO2 - 0.21) / 0.79) * wantPressure) - (((havePO2 - 0.21) / 0.79) * startPressure)).toFixed(1);
        let recipe = {
            gas: 'EAN' + Math.ceil(wantPO2 * 100),
            blendingMethod: 'Partial',
            havePO2: havePO2,
            havePressure: havePressure,
            bleedTo: startPressure === havePressure ? 0 : startPressure,
            o2Pressure: Number(o2),
            wantPO2: wantPO2,
            wantPressure: wantPressure,
            contBlend: 0
        };

        if (havePressure > wantPressure) {
            changeAlert('You have entered that you want a lower pressure in your mix than you already have!')
        } else if ((Number(o2) + Number(startPressure)) > Number(wantPressure)) {
            changeAlert('You have to bleed your current mix!')
        } else {
            clearAlert()
            setRecipe(recipe)
            handleClickOpen()
        }

    }

    const calcContinuousBlending = (havePO2: number, havePressure: number, wantPO2: number, wantPressure: number): void => {
        let bleedTo = needBleed(havePO2, havePressure, wantPO2, wantPressure);
        let bleed = bleedOf(havePO2, wantPO2, wantPressure);
        let startPressure = havePressure;
        if (Number(bleedTo) < 0) {
            startPressure = Number(bleed);
        }
        let contBlend = ((((wantPO2 * wantPressure) - (havePO2 * startPressure)) / (wantPressure - startPressure)) * 100).toFixed(1);
        let recipe = {
            gas: 'EAN' + Math.ceil(wantPO2 * 100),
            blendingMethod: 'Continuous',
            bleedTo: startPressure === havePressure ? 0 : startPressure,
            havePO2: havePO2,
            havePressure: havePressure,
            contBlend: Number(contBlend),
            wantPO2: wantPO2,
            wantPressure: wantPressure,
            o2Pressure: 0
        };

        if (havePressure > wantPressure) {
            changeAlert('You have entered that you want a lower pressure in your mix than you already have!')
        } else if (Number(contBlend) > 40) {
            changeAlert('You have to bleed your current mix, cant fill with higher mix than 40%!')
        } else {
            clearAlert()
            setRecipe(recipe)
            handleClickOpen()
        }
    }

    // @ts-ignore
    // @ts-ignore
    // @ts-ignore
    return (
        <Page>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Typography variant="h4" align="center" mt={2}>{recipe.gas}</Typography>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <Typography variant="h6" align={"center"}>Blending Method: {recipe.blendingMethod}</Typography>
                        <Grid container rowSpacing={1} columnSpacing={2.75} sx={{mb: 2, mt: 1}}>
                            <Grid item xs={6}></Grid>
                            <Grid item xs={3}><Typography fontWeight={800}>Have</Typography></Grid>
                            <Grid item xs={3}><Typography fontWeight={800}>Want</Typography></Grid>
                            <Grid item xs={6}>Oxygen</Grid>
                            <Grid item xs={3}>{Math.round(recipe.havePO2 * 100)} %</Grid>
                            <Grid item xs={3}>{Math.round(recipe.wantPO2 * 100)} %</Grid>
                            <Grid item xs={6}>Pressure</Grid>
                            <Grid item xs={3}>{recipe.havePressure}</Grid>
                            <Grid item xs={3}>{recipe.wantPressure}</Grid>
                        </Grid>
                        <Typography mt={4} mb={1} variant="h6" align={"center"}>Blending instructions</Typography>
                        <Grid container rowSpacing={1} columnSpacing={2.75} sx={{mb: 1}}>
                            <Grid item xs={6}><Typography fontWeight={800}>Action</Typography></Grid>
                            <Grid item xs={3}><Typography fontWeight={800} align={"right"}>+/-</Typography></Grid>
                            <Grid item xs={3}><Typography fontWeight={800} align={"right"}>To</Typography></Grid>
                        </Grid>
                        <Grid container rowSpacing={1} columnSpacing={2.75}>
                            <Grid item xs={6}>Starting with</Grid>
                            <Grid item xs={3}></Grid>
                            <Grid item xs={3}><Typography
                                align={"right"}>{Number(recipe.havePressure).toFixed(1)}</Typography></Grid>
                            {recipe.bleedTo > 0 && (
                                <>
                                    <Grid item xs={6}>Bleed down</Grid>
                                    <Grid item xs={3}>{Number(recipe.bleedTo - recipe.havePressure).toFixed(1)}</Grid>
                                    <Grid item xs={3}>
                                        <Typography align={"right"}>{Number(recipe.bleedTo).toFixed(1)}</Typography>
                                    </Grid>
                                </>)}

                            {blendingMethod === 'partial' ? (
                                <>
                                    {recipe.o2Pressure < 1 && (
                                        <>
                                            <Grid item xs={6}>Add Oxygen</Grid>
                                            <Grid item xs={3}>
                                                <Typography
                                                    align={"right"}>{Number(recipe.o2Pressure).toFixed(1)}</Typography>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Typography
                                                    align={"right"}>{(Number(recipe.havePressure + recipe.o2Pressure)).toFixed(1)}</Typography>
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={6}>Top up with air</Grid>
                                    <Grid item xs={3}><Typography
                                        align={"right"}>{(Number(recipe.bleedTo > 0 ? recipe.wantPressure - recipe.bleedTo : recipe.wantPressure - recipe.havePressure - recipe.o2Pressure )).toFixed(1)}</Typography></Grid>
                                    <Grid item xs={3}><Typography
                                        align={"right"}>{Number(recipe.wantPressure).toFixed(1)}</Typography></Grid>
                                </>
                            ) : (
                                <>
                                    <Grid item xs={6}>{"Add EAN" + recipe.contBlend}</Grid>
                                    <Grid item xs={3}>
                                        <Typography
                                            align={"right"}>{Number(recipe.bleedTo > 0 ? recipe.wantPressure - recipe.bleedTo : recipe.wantPressure - recipe.havePressure).toFixed(1)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography
                                            align={"right"}>{Number(recipe.wantPressure).toFixed(1)}</Typography>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <View>
                <Typography variant="h6" sx={{mt: 1, mb: 1}}>NITROX</Typography>
            </View>
            <View>
                {alert ? <Alert severity="error">{alertDescription}</Alert> : ''}
                <Box sx={{marginTop: 2}}>
                    <ToggleButtonGroup
                        color="primary"
                        value={blendingMethod}
                        exclusive
                        onChange={changeBlendingMethod}
                        aria-label="Platform"
                    >
                        <ToggleButton value="partial">Partial</ToggleButton>
                        <ToggleButton value="continuous">Continuous</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box sx={{marginTop: 5}}>
                    <Typography variant="h6" sx={{mt: 1, mb: 1}}>Start</Typography>
                    <FormControl variant="standard" sx={{m: 2, minWidth: 90}}>
                        <InputLabel id="startMix">Start Mix</InputLabel>
                        <Select
                            labelId="startMix"
                            id="startMix"
                            value={startMix.toString()}
                            label="startMix"
                            onChange={changeStartMix}
                        >
                            {[...Array(81)].map((x, i: number) => (
                                <MenuItem key={'smix' + x} value={20 + i}>{20 + i}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="standard" sx={{m: 2, minWidth: 90}}>
                        <InputLabel id="startPressure">Start Pressure</InputLabel>
                        <Select
                            labelId="startPressure"
                            id="startPressure"
                            value={startPressure.toString()}
                            label="startPressure"
                            onChange={changeStartPressure}
                        >
                            {[...Array(300)].map((x, i: number) => (
                                <MenuItem key={'sp' + x} value={1 + i}>{1 + i}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{marginTop: 5}}>
                    <Typography variant="h6" sx={{mt: 1, mb: 1}}>Target</Typography>
                    <FormControl variant="standard" sx={{m: 2, minWidth: 90}}>
                        <InputLabel id="targetMix">Start Mix</InputLabel>
                        <Select
                            labelId="targetMix"
                            id="targetMix"
                            value={targetMix.toString()}
                            label="targetMix"
                            onChange={changeTargetMix}
                        >
                            {[...Array(81)].map((x, i: number) => (
                                <MenuItem key={'tmix' + x} value={20 + i}>{20 + i}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="standard" sx={{m: 2, minWidth: 90}}>
                        <InputLabel id="targetPressure">Target Pressure</InputLabel>
                        <Select
                            labelId="targetPressure"
                            id="targetPressure"
                            value={targetPressure.toString()}
                            label="targetPressure"
                            onChange={changeTargetPressure}
                        >
                            {[...Array(300)].map((x, i: number) => (
                                <MenuItem key={'tp' + x} value={1 + i}>{1 + i}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{marginTop: 5}}>
                    <Button variant="contained" onClick={calc}>Calculate</Button>
                </Box>
            </View>
        </Page>
    );
}

export default Nitrox;